<template>
  <div class="map-layers-tree-selector__container">
    <CollapseLateral
      orientation="down-left"
      autoclose
      text-tooltip="Capas"
      width="auto"
    >
      <template #icon>
        <span class="material-icons">
          account_tree
        </span>
      </template>
      <template #title>
        <div class="map-layers-tree-selector__header flex flex-row align-items-start">
          <h4 class="ml-2 my-1">
            Capas carreteras
          </h4>
          <span
            v-show="props___.existsCustomLayersSettings"
            v-tooltip.left="'Reniciar transparencias de todas las capas'"
            class="material-icons align-self-end ml-2 mr-1"
            @click="resetAllLayersStyle"
          >
            invert_colors_off
          </span>
        </div>
      </template>
      <LegacyTree
        v-if="props___.legacyTree"
        selection-mode="checkbox"
        :options="props___.layers"
        label-key="description"
        active-key="visible"
        children-key="children"
        identificator-key="id"
        @item-selected="onChangeLayerGroup"
        @set-transparency="setTransparency"
      ></LegacyTree>
      <Tree
        v-if="!props___.legacyTree"
        v-model:selectionKeys="selectedNodes"
        v-model:expandedKeys="expandedNodes"
        class="map-layers-tree-selector__tree-container"
        :value="props___.layers"
        selection-mode="checkbox"
      >
        <template #group="slotProps">
          <b>{{ slotProps.node.description }}</b>
        </template>
        <template #layer="slotProps">
          <div
            @click.stop
          >
            <Panel
              toggleable
              collapsed
            >
              <template #header>
                <div class="map-layers-tree-selector__layer-container flex gap-1">
                  <img
                    class="map-layers-tree-selector__layer-icon"
                    :src="slotProps.node.iconImgUrl"
                  />
                  <span>{{ slotProps.node.description }}</span>
                </div>
              </template>
              <template #default>
                <LayerSettings
                  :layer-id="slotProps.node.layerName"
                  @set-transparency="setTransparency"
                />
              </template>
            </Panel>
          </div>
        </template>
      </Tree>
    </CollapseLateral>
  </div>
</template>

<script setup>
import {ref, computed, inject, watch, watchEffect} from 'vue';
import Tree from 'primevue/tree';
import Panel from 'primevue/panel';
import LayerSettings from '@/components/layer-settings/LayerSettings';
import LegacyTree from '@/components/Tree/Tree';

import {CollapseLateral} from '@sttedra_ui/framework';
// #region Props, Emits
const props___ = defineProps({
  legacyTree: {
    type: Boolean,
    default: () => {
      return false;
    },
  },
  layers: {
    type: Array,
    required: true,
  },
  leafMapLayers: {
    type: Array,
    required: true,
  },
  existsCustomLayersSettings: {
    type: Boolean,
    required: true,
  },
});
const emit = defineEmits(['setTransparency', 'resetLayersStyles']);
// #endregion

// #region Params, Ref, Reactives, Computed
const selectedNodes = ref({});
const isTreeInitialised = ref(false);
const selectedLeafLayers = computed(() => {
  let res = null;
  if (
    !!props___.leafMapLayers?.length &&
    !!selectedNodes?.value &&
    !!isTreeInitialised?.value
  ) {
    res = {};
    const leafIds = props___.leafMapLayers.map((l) => l.id);
    for (const id of leafIds) {
      res[id] = false;
      if (selectedNodes.value[id]?.checked) {
        res[id] = true;
      }
    }
  }
  return res;
});
const expandedNodes = ref({});
const setDefaultStateNode = (node, expandedState, selectedState) => {
  selectedState[node.id] = {
    checked: node.defaultVisible,
    partialChecked: false,
  };
  if (!!node?.children?.length) { // Group node
    expandedState[node.id] = node.defaultOpen;
    for (const child of node.children) {
      setDefaultStateNode(child, expandedState, selectedState);
    }
  }
};
const defaultLayersState = computed(() => {
  let defaultExpandedLayers = null;
  let defaultSelectedLayers = null;
  if (!!props___.layers?.length) {
    defaultExpandedLayers = {};
    defaultSelectedLayers = {};
    for (const rootNode of props___.layers) {
      setDefaultStateNode(rootNode,
        defaultExpandedLayers,
        defaultSelectedLayers);
    }
  }
  return {
    defaultExpandedLayers,
    defaultSelectedLayers,
  };
});
const defaultExpandedLayers = computed(() => {
  let res = null;
  if (!!defaultLayersState?.value?.defaultExpandedLayers) {
    res = defaultLayersState.value.defaultExpandedLayers;
  }
  return res;
});
const defaultSelectedLayers = computed(() => {
  let res = null;
  if (!!defaultLayersState?.value?.defaultSelectedLayers) {
    res = defaultLayersState.value.defaultSelectedLayers;
  }
  return res;
});
// const layersVisibilityList = ref([]);
const maplibreRef = inject('maplibreRef');
const GROUP_RASTER_LAYERS = inject('GROUP_RASTER_LAYERS');
// #endregion

// #region Functions
async function resetAllLayersStyle() {
  emit('resetLayersStyles');
}
async function setTransparency(params) {
  emit('setTransparency', params);
}
function onChangeLayerGroup(item) {
  const layerName = item.layerName;
  const maplibre = maplibreRef?.value;
  if (!!maplibre && !!layerName) {
    if (!item?.rasterInfo) {
      const visibleLayer = item.visible ? 'visible' : 'none';
      maplibre.setLayoutProperty(layerName, 'visibility', visibleLayer);
      if (!!item?.selectorLinked?.length) {
        for (const linkedName of item.selectorLinked) {
          maplibre.setLayoutProperty(linkedName, 'visibility', visibleLayer);
        }
      }
      return;
    }
    if (!!item?.rasterInfo) { // Openlayer layer
      GROUP_RASTER_LAYERS.getLayers().getArray().forEach((layer) => {
        const layerProperties = layer.getProperties();
        if (layerProperties.values.id === layerName) {
          layer.setVisible(item.visible);
        }
      });
    }
  }
}
// #endregion

// #region Effects, Hooks
function initTree() {
  if (!!defaultSelectedLayers?.value &&
      !!defaultExpandedLayers?.value) {
    selectedNodes.value = defaultSelectedLayers.value;
    expandedNodes.value = defaultExpandedLayers.value;
    isTreeInitialised.value = true;
  }
}
watchEffect(() => initTree());

watch(selectedLeafLayers,
  (newSelectedLayers,
    oldSelectedLayers) => {
    if (!!oldSelectedLayers && !!newSelectedLayers) {
      for (const layerId in oldSelectedLayers) {
        if (!!props___.leafMapLayers?.length &&
            newSelectedLayers[layerId] !== oldSelectedLayers[layerId]) {
          const layer = props___.leafMapLayers.find((ll) => ll.id === layerId);
          if (!!layer) {
            // layer visibility change
            layer.visible = newSelectedLayers[layerId];
            onChangeLayerGroup(layer);
          }
        }
      }
    }
    return;
  });
// #endregion

// #region Provide, Expose

// #endregion
</script>

<style lang="scss" scoped>
@import "src/styles";
.map-layers-tree-selector {
  &__container {
    & :deep(.lateral-collapse-container__contain) {
      padding: 0;
      transition: none;
    }
    & :deep(.p-treenode-label) {
      width: 100%;
      font-size: 0.9rem;
    }
    & :deep(.p-panel-content) {
      padding: 0.3rem;
    }
    & :deep(.p-panel-header) {
      padding: 0.3rem;
    }
    & :deep(.p-tree .p-tree-container .p-treenode .p-treenode-content) {
      padding-top: 0.2rem;
      padding-bottom: 0.2rem;
      padding-left: 0.5rem;
      padding-right: 0.5rem;
    }
    & :deep(.p-treenode .p-treenode-content .p-tree-toggler) {
      order: 3;
    }
    & :deep(.p-treenode-leaf .p-treenode-content .p-tree-toggler) {
      display: none;
    }
    & :deep(.lateral-collapse-container__contain) {
      max-width: none;
    }
  }
  &__header {
    padding: 0.3rem;
  }
  &__tree-container {
    width: var(--width-lateral-menu);
    @include min-screen-width($brand__breakpoint--md) {
      width: var(--width-lateral-menu__md);
    }
    display: block;
    padding: 0;
    & :deep(.p-tree-wrapper) {
      max-height: 80dvh;
      overflow-y: auto;
    }
  }
  &__layer-container {
    display: flex;
  }
  &__layer-icon {
    max-height: 20px;
    max-width: 20px;
  }
}
</style>
